import { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * Tiny live-3D viewer for static GLB previews — used by the library
 * objects page + Characters gallery cards.
 *
 * NOT a character runtime — no animations, no audio analyser, no chat.
 * Just: mount canvas → fixed perspective → load GLB → frame it.
 * Disposes WebGL context on unmount so IntersectionObserver-gated
 * mount/unmount can recycle GL contexts safely.
 */
export interface SimpleGlbViewerProps {
  url: string;
  /** Optional Y-axis rotation in degrees (matches `viewportPose.rotationY`). */
  rotationY?: number;
  /** Padding (0..0.4) — how much breathing room around the framed object. */
  framePadding?: number;
  className?: string;
  /**
   * Optional animation configuration — mirrors the model3d schema's
   * `clipLabels` (clipName → operator label) and `defaultClipLabels`
   * (which labels are eligible for the default loop). When set, the
   * viewer plays the first matching clip on a render loop. Without
   * these, the viewer renders a single static frame (same legacy
   * behaviour as before).
   */
  clipLabels?: Record<string, string>;
  defaultClipLabels?: string[];
  /** Fires once the GLB is mounted + framed. Lets the parent fade
   *  the card in only after the model is actually visible. */
  onReady?: () => void;
  /**
   * Turntable mode — drag horizontally to spin the model 360°, with
   * momentum on release (friction decay, like a real turntable).
   * Off by default so gallery thumbnails stay non-interactive; the
   * object-detail modal opts in. Vertical touch still scrolls
   * (touch-action: pan-y).
   */
  interactive?: boolean;
}

export function SimpleGlbViewer({
  url, rotationY = 0, framePadding = 0.15, className,
  clipLabels, defaultClipLabels, onReady, interactive,
}: SimpleGlbViewerProps): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let disposed = false;

    // Renderer.
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    // `true` updates BOTH the canvas drawing buffer AND the CSS dimensions
    // so the canvas fills the container — without this, canvas keeps its
    // default 300×150 inline size and renders in a corner of the div.
    renderer.setSize(container.clientWidth, container.clientHeight, true);
    renderer.setClearColor(0x000000, 0);
    // sRGB output — without this three.js r152+ defaults to linear
    // colour space, which makes baseColor textures look washed-out
    // and dark on screen. The 3D edit Preview tab sets this too;
    // matching it here keeps gallery thumbnails visually consistent
    // with their Preview rendering.
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    // Belt-and-braces: ensure the canvas fills any size container reflow
    // does to it after mount (Three.js's true-flag only sets pixel CSS).
    const canvas = renderer.domElement;
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    container.appendChild(canvas);

    // Animation state — only created if the GLB has clips AND the
    // caller passed clipLabels / defaultClipLabels (i.e. they want
    // a moving thumbnail, not a static one).
    let mixer: THREE.AnimationMixer | null = null;
    let rafId: number | null = null;
    let lastTime = 0;

    // Scene + camera + lights. Match the model3d-runtime's lighting
    // intensities (key 2.2 + hemisphere 1.4 with warm sky / cool
    // ground) so gallery thumbnails read the same brightness as the
    // Preview tab. Earlier ambient 0.6 + directional 0.8 left
    // PBR materials looking flat + grey because the unlit
    // contribution dominated the diffuse.
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, container.clientWidth / container.clientHeight, 0.01, 1000);
    camera.position.set(0, 0, 2);
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(2.5, 4, 3);
    scene.add(key);
    scene.add(new THREE.HemisphereLight(0xf5ead8, 0x283540, 1.4));

    // Single static render — no animation loop, saves CPU.
    const renderOnce = (): void => {
      if (disposed) return;
      renderer.render(scene, camera);
    };

    // Animation loop — only spun up after a clip is found + started.
    // Throttled to ~24 fps (`elapsed > 1/24`) to keep N×3D thumbnails
    // affordable on the gallery page; smooth 60 fps belongs in the
    // edit / preview pane.
    const FRAME_BUDGET_SEC = 1 / 24;
    const tick = (nowMs: number): void => {
      if (disposed) return;
      // First frame just initialises the time baseline. Without this
      // the `lastTime === 0` branch left dt at 0, the budget check
      // never passed, and lastTime never got set — animation stayed
      // frozen at frame 0 forever (the "T-pose on gallery cards"
      // operator complaint 2026-05-14).
      if (lastTime === 0) {
        lastTime = nowMs;
        rafId = requestAnimationFrame(tick);
        return;
      }
      const dt = (nowMs - lastTime) / 1000;
      if (dt >= FRAME_BUDGET_SEC) {
        if (mixer) mixer.update(dt);
        renderer.render(scene, camera);
        lastTime = nowMs;
      }
      rafId = requestAnimationFrame(tick);
    };

    // ── Turntable (interactive mode only) ──────────────────────────
    // Drag spins the model around Y; release hands the last per-event
    // delta to a friction loop (×0.94/frame) so the spin coasts and
    // slows like a real weighted turntable. When the clip-animation
    // raf loop is running we only mutate rotation (that loop renders);
    // otherwise we render on demand to keep the static-thumbnail CPU
    // saving intact.
    let modelRoot: THREE.Object3D | null = null;
    let dragging = false;
    let dragLastX = 0;
    let spinVelocity = 0; // radians per frame
    let inertiaRaf: number | null = null;
    const reducedMotion = typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const startInertia = (): void => {
      if (reducedMotion || inertiaRaf !== null) return;
      const step = (): void => {
        inertiaRaf = null;
        if (disposed || !modelRoot || dragging) return;
        spinVelocity *= 0.94;
        if (Math.abs(spinVelocity) < 0.0004) return;
        modelRoot.rotation.y += spinVelocity;
        if (rafId === null) renderOnce();
        inertiaRaf = requestAnimationFrame(step);
      };
      inertiaRaf = requestAnimationFrame(step);
    };

    // Museum turntable (interactive mode): slow 0.25 rad/s display
    // rotation while the viewer is untouched; any drag claims control
    // instantly, and the turntable resumes 4s after the hands leave.
    // Throttled to ~30fps and only renders when the clip loop isn't
    // already rendering. Off under reduced motion.
    let autoRaf: number | null = null;
    let lastInteractTs = -Infinity;
    let autoLastMs = 0;
    const AUTO_SPEED = 0.25; // rad/s
    const autoStep = (nowMs: number): void => {
      autoRaf = null;
      if (disposed) return;
      const dt = autoLastMs === 0 ? 0 : (nowMs - autoLastMs) / 1000;
      autoLastMs = nowMs;
      const idle = !dragging
        && Math.abs(spinVelocity) < 0.0004
        && (performance.now() - lastInteractTs > 4000);
      if (idle && modelRoot && dt > 0 && dt < 0.2) {
        modelRoot.rotation.y += AUTO_SPEED * Math.min(dt, 1 / 30);
        if (rafId === null) renderOnce();
      }
      autoRaf = requestAnimationFrame(autoStep);
    };
    if (interactive && !reducedMotion) {
      autoRaf = requestAnimationFrame(autoStep);
    }

    const onPointerDown = (e: PointerEvent): void => {
      if (!modelRoot) return;
      dragging = true;
      lastInteractTs = performance.now();
      dragLastX = e.clientX;
      spinVelocity = 0;
      if (inertiaRaf !== null) { cancelAnimationFrame(inertiaRaf); inertiaRaf = null; }
      canvas.style.cursor = 'grabbing';
      try { canvas.setPointerCapture(e.pointerId); } catch { /* noop */ }
    };
    const onPointerMove = (e: PointerEvent): void => {
      if (!dragging || !modelRoot) return;
      lastInteractTs = performance.now();
      const dx = e.clientX - dragLastX;
      dragLastX = e.clientX;
      const delta = dx * 0.012;
      modelRoot.rotation.y += delta;
      spinVelocity = delta;
      if (rafId === null) renderOnce();
    };
    const onPointerUp = (): void => {
      if (!dragging) return;
      dragging = false;
      lastInteractTs = performance.now(); // turntable resumes 4s from release
      canvas.style.cursor = 'grab';
      startInertia();
    };
    if (interactive) {
      canvas.style.touchAction = 'pan-y';
      canvas.style.cursor = 'grab';
      canvas.addEventListener('pointerdown', onPointerDown);
      canvas.addEventListener('pointermove', onPointerMove);
      canvas.addEventListener('pointerup', onPointerUp);
      canvas.addEventListener('pointercancel', onPointerUp);
    }

    // Resize handling — listen on a ResizeObserver since the container
    // might be in a grid that changes size on window resize.
    const ro = new ResizeObserver(() => {
      if (disposed) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w <= 0 || h <= 0) return;
      renderer.setSize(w, h, true);
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderOnce();
    });
    ro.observe(container);

    // Load GLB.
    const loader = new GLTFLoader();
    loader.load(
      url,
      (gltf) => {
        if (disposed) return;
        const root = gltf.scene;
        root.rotation.y = (rotationY * Math.PI) / 180;
        scene.add(root);
        modelRoot = root;

        // Frame the model. Use the bounding BOX centre (geometric mid)
        // as lookAt target — sphere centre can be off for models with
        // raised arms / asymmetric meshes. Distance derived from the
        // larger of horizontal/vertical extent so both axes fit.
        const box = new THREE.Box3().setFromObject(root);
        const center = new THREE.Vector3();
        const size = new THREE.Vector3();
        box.getCenter(center);
        box.getSize(size);
        const maxExtent = Math.max(size.x, size.y, size.z, 0.01);
        const fov = (camera.fov * Math.PI) / 180;
        // half-height of view frustum at unit distance = tan(fov/2)
        // distance to fit maxExtent vertically: maxExtent/2 / tan(fov/2)
        // multiply by aspect-safe margin
        const aspect = camera.aspect || 1;
        const fitHeight = (maxExtent / 2) / Math.tan(fov / 2);
        const fitWidth = fitHeight / Math.min(aspect, 1);
        const distance = Math.max(fitHeight, fitWidth) * (1 + framePadding);
        // Centred straight-on view (no horizontal offset — was causing
        // models to render off to one side when card aspect was tall).
        // Small upward tilt so the camera is slightly above eye-level.
        camera.position.set(center.x, center.y + maxExtent * 0.1, center.z + distance);
        camera.lookAt(center);
        camera.near = Math.max(0.001, distance * 0.01);
        camera.far = distance * 10;
        camera.updateProjectionMatrix();
        renderOnce();

        // Animation path. Permissive resolution order (most
        // operators won't have labelled every clip):
        //   1. clip whose label (clipLabels[clipName]) matches an
        //      entry in defaultClipLabels — exact label binding
        //   2. clip whose NAME directly matches an entry in
        //      defaultClipLabels — when the operator put the raw
        //      clip name in defaultClipLabels instead of a label
        //   3. first animation in the GLB — last-resort fallback
        //      so cards still "move" even when nothing is
        //      configured. Better visual than a static T-pose.
        const clips = gltf.animations ?? [];
        if (clips.length > 0) {
          const defaultLabelSet = new Set(defaultClipLabels ?? []);
          let match = clips.find((cl) => {
            const label = clipLabels?.[cl.name];
            return label !== undefined && defaultLabelSet.has(label);
          });
          if (!match) {
            match = clips.find((cl) => defaultLabelSet.has(cl.name));
          }
          if (!match) {
            match = clips[0];
          }
          if (match) {
            mixer = new THREE.AnimationMixer(root);
            const action = mixer.clipAction(match);
            action.setLoop(THREE.LoopRepeat, Infinity);
            action.play();
            // Stagger the loop start by 0-1s so 10 thumbnails don't
            // all hit `requestAnimationFrame` on the exact same
            // frame — spreads CPU spikes across the page.
            window.setTimeout(() => {
              if (disposed) return;
              rafId = requestAnimationFrame(tick);
            }, Math.random() * 1000);
          }
        }
        try { onReady?.(); } catch { /* noop */ }
      },
      undefined,
      (err) => {
        // Silent failure — caller can fall back to a placeholder.
        console.warn('[SimpleGlbViewer] GLB load failed', url, err);
      },
    );

    return () => {
      disposed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (inertiaRaf !== null) cancelAnimationFrame(inertiaRaf);
      if (autoRaf !== null) cancelAnimationFrame(autoRaf);
      if (interactive) {
        canvas.removeEventListener('pointerdown', onPointerDown);
        canvas.removeEventListener('pointermove', onPointerMove);
        canvas.removeEventListener('pointerup', onPointerUp);
        canvas.removeEventListener('pointercancel', onPointerUp);
      }
      if (mixer) mixer.stopAllAction();
      ro.disconnect();
      // Dispose scene resources.
      scene.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else if (mat) mat.dispose();
      });
      renderer.dispose();
      try {
        if (renderer.domElement.parentNode === container) {
          container.removeChild(renderer.domElement);
        }
      } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, rotationY, interactive, JSON.stringify(clipLabels), JSON.stringify(defaultClipLabels)]);

  return <div ref={containerRef} className={className} style={{ width: '100%', height: '100%', minHeight: 120, position: 'relative', overflow: 'hidden' }} />;
}
